import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiGet, apiPatch } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import type { TaxMode } from '../../api/types';

interface FinanceSettings {
  ownerDailyRate: number | null;
  defaultTaxMode: TaxMode | null;
  defaultTaxRate: number | null;
  defaultTaxCoefficient: number | null;
}

const inputStyle: React.CSSProperties = {
  background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
  fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', outline: 'none', width: '100%',
};

const selectStyle: React.CSSProperties = {
  background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
  fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', outline: 'none', cursor: 'pointer',
};

export function FinanceSection() {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    ownerDailyRate: '',
    defaultTaxMode: '' as '' | TaxMode,
    defaultTaxRate: '',
    defaultTaxCoefficient: '',
  });
  const [loaded, setLoaded] = useState(false);

  const { data: settings, isLoading } = useQuery<FinanceSettings>({
    queryKey: ['user-settings-finance'],
    queryFn: () => apiGet<FinanceSettings>('/user-settings/finance'),
  });

  React.useEffect(() => {
    if (settings && !loaded) {
      setForm({
        ownerDailyRate: settings.ownerDailyRate != null ? String(settings.ownerDailyRate) : '',
        defaultTaxMode: settings.defaultTaxMode ?? '',
        defaultTaxRate: settings.defaultTaxRate != null ? String(settings.defaultTaxRate) : '',
        defaultTaxCoefficient: settings.defaultTaxCoefficient != null ? String(settings.defaultTaxCoefficient) : '',
      });
      setLoaded(true);
    }
  }, [settings, loaded]);

  const saveMutation = useMutation({
    mutationFn: () => apiPatch('/user-settings/finance', {
      ownerDailyRate: form.ownerDailyRate !== '' ? Number(form.ownerDailyRate) : null,
      defaultTaxMode: form.defaultTaxMode || null,
      defaultTaxRate: form.defaultTaxMode && form.defaultTaxRate !== '' ? Number(form.defaultTaxRate) : null,
      defaultTaxCoefficient: form.defaultTaxMode === 'FORFETTARIO' && form.defaultTaxCoefficient !== '' ? Number(form.defaultTaxCoefficient) : null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-settings-finance'] });
      toast.success('Impostazioni finanziarie salvate');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const showTaxFields = form.defaultTaxMode !== '';
  const showCoefficient = form.defaultTaxMode === 'FORFETTARIO';

  if (isLoading) {
    return <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Caricamento…</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
        Impostazioni predefinite usate nel calcolo dei margini. Possono essere sovrascritte per ogni singolo evento.
      </p>

      <div>
        <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 500, marginBottom: '0.375rem' }}>
          Compenso giornaliero titolare (€)
        </label>
        <input
          type="number"
          min="0"
          step="0.01"
          placeholder="Es. 300"
          value={form.ownerDailyRate}
          onChange={e => setForm(f => ({ ...f, ownerDailyRate: e.target.value }))}
          style={{ ...inputStyle, maxWidth: '12rem' }}
        />
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginTop: '0.25rem' }}>
          Usato per calcolare il compenso quando sei presente all'evento (spesa COMPENSO_TITOLARE con ownerPresent).
        </p>
      </div>

      <hr style={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      <div>
        <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 500, marginBottom: '0.375rem' }}>
          Regime fiscale predefinito
        </label>
        <select
          value={form.defaultTaxMode}
          onChange={e => setForm(f => ({ ...f, defaultTaxMode: e.target.value as '' | TaxMode, defaultTaxRate: '', defaultTaxCoefficient: '' }))}
          style={{ ...selectStyle, minWidth: '14rem' }}
        >
          <option value="">Nessuno (non calcolare imposte)</option>
          <option value="FORFETTARIO">Forfettario</option>
          <option value="ORDINARIO">Ordinario</option>
        </select>
      </div>

      {showTaxFields && (
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '10rem' }}>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 500, marginBottom: '0.375rem' }}>
              Aliquota imposta (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              placeholder="Es. 15"
              value={form.defaultTaxRate}
              onChange={e => setForm(f => ({ ...f, defaultTaxRate: e.target.value }))}
              style={{ ...inputStyle }}
            />
          </div>

          {showCoefficient && (
            <div style={{ flex: 1, minWidth: '10rem' }}>
              <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 500, marginBottom: '0.375rem' }}>
                Coefficiente di redditività (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                placeholder="Es. 78"
                value={form.defaultTaxCoefficient}
                onChange={e => setForm(f => ({ ...f, defaultTaxCoefficient: e.target.value }))}
                style={{ ...inputStyle }}
              />
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginTop: '0.25rem' }}>
                Forfettario: base imponibile = margine lordo × coeff%
              </p>
            </div>
          )}
        </div>
      )}

      <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending} style={{ alignSelf: 'flex-start' }}>
        Salva impostazioni finanza
      </Button>
    </div>
  );
}
